import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { certificateAPI } from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';

const CertificateHistory = () => {
    const { t } = useLanguage();
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        from: 0,
        to: 0,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 15;
    const [search, setSearch] = useState({
        certificate_no: '',
        husband_name: '',
        wife_name: '',
        mobile: '',
        from_date: '',
        to_date: '',
        status: '',
    });

    useEffect(() => {
        fetchCertificates();
    }, [currentPage]);

    const fetchCertificates = async () => {
        setLoading(true);
        try {
            const response = await certificateAPI.list({ page: currentPage, per_page: perPage });
            setCertificates(response.data.data || response.data);
            // Set pagination from Laravel response
            if (response.data.meta) {
                setPagination({
                    current_page: response.data.meta.current_page,
                    last_page: response.data.meta.last_page,
                    per_page: response.data.meta.per_page,
                    total: response.data.meta.total,
                    from: response.data.meta.from || (response.data.meta.current_page - 1) * response.data.meta.per_page + 1,
                    to: response.data.meta.to || Math.min(response.data.meta.current_page * response.data.meta.per_page, response.data.meta.total),
                });
            } else if (response.data.current_page) {
                // Laravel default pagination structure
                setPagination({
                    current_page: response.data.current_page,
                    last_page: response.data.last_page,
                    per_page: response.data.per_page,
                    total: response.data.total,
                    from: response.data.from,
                    to: response.data.to,
                });
            }
        } catch (error) {
            toast.error(t('certificate.failedLoad') || 'Failed to load certificates');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearch = async () => {
        setLoading(true);
        setCurrentPage(1);
        try {
            const params = { page: 1, per_page: perPage };
            Object.keys(search).forEach((key) => {
                if (search[key]) {
                    params[key] = search[key];
                }
            });
            const response = await certificateAPI.search(params);
            setCertificates(response.data.data || response.data);
            if (response.data.meta) {
                setPagination({
                    current_page: response.data.meta.current_page,
                    last_page: response.data.meta.last_page,
                    per_page: response.data.meta.per_page,
                    total: response.data.meta.total,
                    from: response.data.meta.from,
                    to: response.data.meta.to,
                });
            } else if (response.data.current_page) {
                setPagination({
                    current_page: response.data.current_page,
                    last_page: response.data.last_page,
                    per_page: response.data.per_page,
                    total: response.data.total,
                    from: response.data.from,
                    to: response.data.to,
                });
            }
        } catch (error) {
            toast.error(t('certificate.searchFailed') || 'Search failed');
        } finally {
            setLoading(false);
        }
    };

    // Generate page numbers with ellipsis for large page counts
    const generatePageNumbers = () => {
        const pages = [];
        const total = pagination.last_page;
        const current = currentPage;

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            if (current <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push('...');
                pages.push(total);
            } else if (current >= total - 3) {
                pages.push(1);
                pages.push('...');
                for (let i = total - 4; i <= total; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = current - 1; i <= current + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(total);
            }
        }

        return pages;
    };

    const handlePrintCertificate = (id) => {
        // Open print page in new tab
        window.open(`/print-certificate/${id}`, '_blank');
    };

    const handleWhatsAppShare = async (id) => {
        try {
            const response = await certificateAPI.whatsappLink(id);
            const { link, qr_image_url } = response.data;

            // If browser supports Web Share API and QR image is available, share text + image
            if (qr_image_url && navigator.share && navigator.canShare) {
                try {
                    // Fetch the QR code image as a blob (public endpoint, no auth needed)
                    const imageResponse = await fetch(qr_image_url);
                    if (imageResponse.ok) {
                        const blob = await imageResponse.blob();
                        const file = new File([blob], 'qr-code.png', { type: blob.type || 'image/png' });

                        // Decode wa.me link to get the raw text message
                        const url = new URL(link);
                        const textMessage = url.searchParams.get('text') || '';

                        const shareData = {
                            text: textMessage,
                            files: [file],
                        };

                        if (navigator.canShare(shareData)) {
                            await navigator.share(shareData);
                            return; // Successfully shared with image
                        }
                    }
                } catch (shareErr) {
                    // User cancelled or share failed — fall through to text-only link
                    if (shareErr.name === 'AbortError') return;
                    console.warn('Web Share API failed, falling back to link:', shareErr);
                }
            }

            // Fallback: open WhatsApp with text-only message
            window.open(link, '_blank');
        } catch (error) {
            toast.error(t('certificate.whatsappFailed') || 'Failed to generate WhatsApp link');
        }
    };

    const clearSearch = () => {
        setSearch({
            certificate_no: '',
            husband_name: '',
            wife_name: '',
            mobile: '',
            from_date: '',
            to_date: '',
            status: '',
        });
        setCurrentPage(1);
        fetchCertificates();
    };

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-4 sm:p-6">
                <h1 className="text-2xl font-bold text-gray-800 mb-6">
                    {t('sidebar.certificateHistory')}
                </h1>

                {/* Search Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                    <input
                        type="text"
                        placeholder={t('certificate.certificateNo')}
                        value={search.certificate_no}
                        onChange={(e) =>
                            setSearch({ ...search, certificate_no: e.target.value })
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                        type="text"
                        placeholder={t('certificate.husbandName') || 'Husband Name'}
                        value={search.husband_name}
                        onChange={(e) =>
                            setSearch({ ...search, husband_name: e.target.value })
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                        type="text"
                        placeholder={t('certificate.wifeName') || 'Wife Name'}
                        value={search.wife_name}
                        onChange={(e) =>
                            setSearch({ ...search, wife_name: e.target.value })
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                        type="text"
                        placeholder={t('certificate.husbandMobile') || 'Mobile'}
                        value={search.mobile}
                        onChange={(e) => setSearch({ ...search, mobile: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                        type="date"
                        placeholder="From"
                        value={search.from_date}
                        onChange={(e) =>
                            setSearch({ ...search, from_date: e.target.value })
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                        type="date"
                        placeholder="To"
                        value={search.to_date}
                        onChange={(e) =>
                            setSearch({ ...search, to_date: e.target.value })
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <select
                        value={search.status}
                        onChange={(e) => setSearch({ ...search, status: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="">{t('certificate.allStatus')}</option>
                        <option value="active">{t('certificate.active')}</option>
                        <option value="cancelled">{t('certificate.cancelled')}</option>
                    </select>
                    <div className="flex gap-2">
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            {t('common.search')}
                        </button>
                        <button
                            onClick={clearSearch}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                            {t('common.clear')}
                        </button>
                    </div>
                </div>

                {/* Certificates Table */}
                {loading ? (
                    <LoadingSpinner />
                ) : certificates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        {t('common.noData')}
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('certificate.certificateNo')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('certificate.husbandName') || 'Husband Name'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('certificate.wifeName') || 'Wife Name'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('certificate.marriageDate')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('certificate.status')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('common.actions')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {certificates.map((cert) => (
                                        <tr key={cert.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                                                {cert.certificate_no}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {cert.husband_name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {cert.wife_name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {cert.marriage_date || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`px-2 py-1 text-xs rounded-full ${
                                                        cert.status === 'active'
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-red-100 text-red-800'
                                                    }`}
                                                >
                                                    {cert.status === 'active'
                                                        ? t('certificate.active')
                                                        : t('certificate.cancelled')}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                                                <button
                                                    onClick={() => handlePrintCertificate(cert.id)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    🖨️ {t('certificate.print') || 'Print'}
                                                </button>
                                                <button
                                                    onClick={() => handleWhatsAppShare(cert.id)}
                                                    className="text-green-600 hover:text-green-800"
                                                >
                                                    {t('certificate.shareWhatsapp')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="md:hidden space-y-4">
                            {certificates.map((cert) => (
                                <div key={cert.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-bold text-blue-600">{cert.certificate_no}</span>
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                            cert.status === 'active'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {cert.status === 'active' ? t('certificate.active') : t('certificate.cancelled')}
                                        </span>
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('certificate.husbandName') || 'Husband'}:</span>
                                            <span className="font-medium text-gray-900">{cert.husband_name}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('certificate.wifeName') || 'Wife'}:</span>
                                            <span className="font-medium text-gray-900">{cert.wife_name}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('certificate.marriageDate')}:</span>
                                            <span className="text-gray-700">{cert.marriage_date || '-'}</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-3 mt-3 pt-3 border-t border-gray-200">
                                        <button
                                            onClick={() => handlePrintCertificate(cert.id)}
                                            className="flex-1 text-center px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                                        >
                                            🖨️ {t('certificate.print') || 'Print'}
                                        </button>
                                        <button
                                            onClick={() => handleWhatsAppShare(cert.id)}
                                            className="flex-1 text-center px-3 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
                                        >
                                            {t('certificate.shareWhatsapp')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {pagination.total > 0 && (
                            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
                                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                    <div>
                                        <p className="text-sm text-gray-700">
                                            Showing <span className="font-medium">{pagination.from}</span> to{' '}
                                            <span className="font-medium">{pagination.to}</span> of{' '}
                                            <span className="font-medium">{pagination.total}</span> results
                                        </p>
                                    </div>
                                    <div>
                                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                            {/* Previous Button */}
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                                                    currentPage === 1
                                                        ? 'text-gray-300 cursor-not-allowed'
                                                        : 'text-gray-500 hover:bg-gray-50'
                                                }`}
                                            >
                                                ‹ Prev
                                            </button>

                                            {/* Page Numbers */}
                                            {generatePageNumbers().map((page) => (
                                                <button
                                                    key={page}
                                                    onClick={() => handlePageChange(page)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                        page === currentPage
                                                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                    } ${page === '...' ? 'cursor-default' : ''}`}
                                                    disabled={page === '...'}
                                                >
                                                    {page}
                                                </button>
                                            ))}

                                            {/* Next Button */}
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === pagination.last_page}
                                                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                                                    currentPage === pagination.last_page
                                                        ? 'text-gray-300 cursor-not-allowed'
                                                        : 'text-gray-500 hover:bg-gray-50'
                                                }`}
                                            >
                                                Next ›
                                            </button>
                                        </nav>
                                    </div>
                                </div>

                                {/* Mobile Pagination */}
                                <div className="flex items-center justify-between sm:hidden">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                            currentPage === 1
                                                ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                                : 'text-gray-700 bg-white hover:bg-gray-50'
                                        }`}
                                    >
                                        Previous
                                    </button>
                                    <span className="text-sm text-gray-700">
                                        Page {currentPage} of {pagination.last_page}
                                    </span>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === pagination.last_page}
                                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                            currentPage === pagination.last_page
                                                ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                                : 'text-gray-700 bg-white hover:bg-gray-50'
                                        }`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CertificateHistory;